import {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';

import {getOptionsGroupingUIProcessed} from '@amblique/king-living/app/utils/product-utils';

export const STEP_MESSAGES = {
  cover: {defaultMessage: 'Choose your cover', id: 'customize_panel.step.cover.title'},
  cushion: {defaultMessage: 'Choose your cushion', id: 'customize_panel.step.cushion.title'},
  cushions: {defaultMessage: 'Choose your cushions', id: 'customize_panel.step.cushions.title'},
  shelves: {defaultMessage: 'Choose your shelves', id: 'customize_panel.step.shelves.title'},
};

/** Known SCAPI/BM cover item-type tabs → static descriptors for formatjs extraction. */
const COVER_ITEM_TYPE_MESSAGES = {
  chenille: {defaultMessage: 'Chenille', id: 'customize_panel.cover.item_type.chenille'},
  fabric: {defaultMessage: 'Fabric', id: 'customize_panel.cover.item_type.fabric'},
  leather: {defaultMessage: 'Leather', id: 'customize_panel.cover.item_type.leather'},
  linen: {defaultMessage: 'Linen', id: 'customize_panel.cover.item_type.linen'},
  suede: {defaultMessage: 'Suede', id: 'customize_panel.cover.item_type.suede'},
  velvet: {defaultMessage: 'Velvet', id: 'customize_panel.cover.item_type.velvet'},
  vinyl: {defaultMessage: 'Vinyl', id: 'customize_panel.cover.item_type.vinyl'},
};

/**
 * Localised label for a cover group's itemType (tab under "Choose your finish").
 *
 * @param {import('react-intl').IntlShape} intl
 * @param {string} [itemType]
 * @returns {string}
 */
export function formatCoverItemTypeLabel(intl, itemType) {
  if (itemType == null || typeof itemType !== 'string') {
    return itemType ?? '';
  }

  const key = itemType.trim().toLowerCase();
  const msg = COVER_ITEM_TYPE_MESSAGES[key];

  if (!msg) {
    return itemType;
  }

  return intl.formatMessage(msg);
}

/**
 * Build the ordered list of customize steps from a product.
 * Returns cover step first, then one step per option container.
 *
 * Layout decision is identical for Complex and Simple PDP (KINDW-1124):
 * a single-value container resolves to radio layout (then swatch-vs-radio inside
 * <OptionStepRadio> per product data), and a multi-value container resolves to
 * slot-cards (per TSD - PDP Simple §"Configurable Options" → "Mostly same as
 * TSD - PDP Complex#Configurable-options").
 *
 * The `isSimplePDP` option is accepted for forward compatibility but is no
 * longer used by buildSteps itself — kept on the signature so call sites that
 * still pass it (Simple PDP `<CustomizeStepsInline>`) don't need a churn change.
 *
 * @param {Object} product - SFCC product with c_optionsGroupingUIProcessed
 * @param {Object} [options] - Build options
 * @param {boolean} [options.isSimplePDP=false] - Reserved for future use; ignored.
 * @returns {Array} Array of step descriptor objects
 */

export function buildSteps(product, {isSimplePDP = false} = {}) {
  const optionContainers = getOptionsGroupingUIProcessed(product) || [];

  const coverStep = {
    hasData: (groups) => {
      if (!groups || groups.length === 0) return false;

      // Check if at least one group has at least one orderable variant
      return groups.some((group) => {
        // Check byColor variants
        const hasOrderableInColor = group.byColor?.some((colorGroup) => colorGroup.variants?.some((v) => v.orderable === true));

        if (hasOrderableInColor) return true;

        // Check byCollection variants
        const hasOrderableInCollection = group.byCollection?.some((collectionGroup) => collectionGroup.variants?.some((v) => v.orderable === true));

        return hasOrderableInCollection;
      });
    },
    id: 'cover',
    layoutType: 'cover',
    titleKey: 'customize_panel.step.cover.title',
  };

  const optionSteps = optionContainers.map((container) => ({
    container,
    customTitle: container.containerName,
    hasData: () => {
      if (!container.values || container.values.length === 0) return false;

      return container.values.some(
        (value) => value.isVisible !== false && Array.isArray(value.products) && value.products.length > 0
      );
    },
    id: container.containerId,
    // Layout decision applies to BOTH Complex and Simple PDP (KINDW-1124 / TSD §"Configurable Options"):
    //   - Single recommendationTypeId  → radio layout. <OptionStepRadio> then makes the final
    //     swatch-vs-radio decision once recommendation product data is loaded (per TSD swatch
    //     layout: single name line / no extra price; radio: two name lines or extra price > 0).
    //   - Multiple recommendationTypeIds → slot-cards. Each slot renders as a "Choose" placeholder
    //     row that opens the PDP-07 product picker sub-view, exactly like Complex PDP. Per TSD
    //     "Mostly same as TSD - PDP Complex#Configurable-options" + FSD §SECTION-PDP-SIMPLE-08.2:
    //     "Clicking on 'Choose' will bring up the flyout as detailed in PDP-07: Customise Your Own".
    layoutType: container.values.length === 1 ? 'radio' : 'slot-cards',
  }));

  return [coverStep, ...optionSteps];
}

/**
 * Steps actually rendered in the customize UI.
 * Excludes:
 * - Option containers with isVisible === false
 * - Option containers with empty values array (e.g. extra container with only optional add-ons)
 *
 * Used for panel header "current / total" so totals match what the user sees (Point 29).
 *
 * @param {Array} steps - From buildSteps()
 * @returns {Array}
 */
export function getVisibleCustomizeSteps(steps) {
  if (!steps?.length) return [];

  return steps.filter((s) => {
    // Exclude steps with isVisible === false
    if (s.container && s.container.isVisible === false) return false;

    // Exclude steps with empty values array (no products to select)
    // This handles cases like "extra" container where all values are optional add-ons
    // and have been moved to optionalAddOns array, leaving values empty
    if (s.container && s.container.values && s.container.values.length === 0) return false;

    return true;
  });
}

/**
 * 1-based index of a step among visible customize steps only.
 *
 * @param {string} stepId
 * @param {Array} steps - Full list from buildSteps()
 * @returns {number} 1..N, or 1 if stepId not found among visible (defensive)
 */
export function getVisibleStepDisplayIndex(stepId, steps) {
  const visible = getVisibleCustomizeSteps(steps);
  const idx = visible.findIndex((s) => s.id === stepId);

  return idx >= 0 ? idx + 1 : 1;
}

/** BM container name / id for the main shelves step only (not per-slot "Shelf 1 finish"). */
const CHOOSE_YOUR_SHELVES_NAME_RE = /choose\s+your\s+shelves?\b/i;

/**
 * True only for the "Choose your shelves" step (slot list like screenshot), not other steps
 * that merely contain "shelf" (e.g. finish picker titles).
 *
 * Matches: containerId `shelves`, or container name matching "choose your shelf(es)".
 *
 * @param {Object} [step] - Step from buildSteps() (not cover)
 * @returns {boolean}
 */
export function isChooseYourShelvesCustomizeStep(step) {
  if (!step || step.id === 'cover') return false;

  const idNorm = String(step.id || '').trim().toLowerCase();

  if (idNorm === 'shelves') return true;

  const containerIdNorm = String(step.container?.containerId || '').trim().toLowerCase();

  if (containerIdNorm === 'shelves') return true;

  const titleBlob = [step.customTitle, step.container?.containerName]
    .filter(Boolean)
    .map((s) => String(s))
    .join(' ');

  return CHOOSE_YOUR_SHELVES_NAME_RE.test(titleBlob);
}

/** BM container name / id for the main cushions step (not unrelated copy containing "cushion"). */
const CHOOSE_YOUR_CUSHIONS_NAME_RE = /choose\s+your\s+cushions?\b/i;

/**
 * True for the "Choose your cushions" step (container id or title pattern).
 *
 * @param {Object} [step] - Step from buildSteps() (not cover)
 * @returns {boolean}
 */
function isChooseYourCushionsCustomizeStep(step) {
  if (!step || step.id === 'cover') return false;

  const idNorm = String(step.id || '').trim().toLowerCase();

  if (idNorm === 'cushions' || idNorm === 'cushion') return true;

  const containerIdNorm = String(step.container?.containerId || '').trim().toLowerCase();

  if (containerIdNorm === 'cushions' || containerIdNorm === 'cushion') return true;

  const titleBlob = [step.customTitle, step.container?.containerName]
    .filter(Boolean)
    .map((s) => String(s))
    .join(' ');

  return CHOOSE_YOUR_CUSHIONS_NAME_RE.test(titleBlob);
}

/**
 * Resolve MessageDescriptor for a customize step heading.
 * Prefers catalog message ids over BM `containerName` (often English-only) when the step
 * matches known shelf/cushion patterns or `containerId` keys in {@link STEP_MESSAGES}.
 *
 * @param {Object} step
 * @returns {import('react-intl').MessageDescriptor|null}
 */
export function resolveStepTitleMessage(step) {
  if (!step) return null;

  const direct = STEP_MESSAGES[step.id];

  if (direct) {
    return direct;
  }

  if (isChooseYourShelvesCustomizeStep(step)) {
    return STEP_MESSAGES.shelves;
  }

  if (isChooseYourCushionsCustomizeStep(step)) {
    const titleBlob = [step.customTitle, step.container?.containerName]
      .filter(Boolean)
      .map((s) => String(s))
      .join(' ');
    const isPluralCushions = /\bchoose\s+your\s+cushions\b/i.test(titleBlob);

    return isPluralCushions ? STEP_MESSAGES.cushions : STEP_MESSAGES.cushion;
  }

  return null;
}

/**
 * Get the display title for a step.
 *
 * @param {Object} step - Step descriptor
 * @param {Object} intl - react-intl intl object
 * @returns {string} Localised step title
 */
export function getStepTitle(step, intl) {
  const msg = resolveStepTitleMessage(step);

  if (msg) {
    return intl.formatMessage(msg);
  }

  if (step.customTitle) {
    return step.customTitle;
  }

  return step.id;
}

/**
 * Stable token for BEM modifier / data attributes: `customize-steps-slot-card--{kind}`.
 * Use for fonts or layout that differ by cover vs cushions vs shelves vs other option steps.
 *
 * @param {Object} [step] - Step from buildSteps() (cover or option step)
 * @returns {string} kebab-style token: `cover` | `cushions` | `shelves` | `extra` | slug from `id` | `other`
 */
export function getCustomizeStepSlotKind(step) {
  if (!step) {
    return 'other';
  }

  if (step.id === 'cover' || step.layoutType === 'cover') {
    return 'cover';
  }

  if (isChooseYourCushionsCustomizeStep(step)) {
    return 'cushion';
  }

  if (isChooseYourShelvesCustomizeStep(step)) {
    return 'shelves';
  }

  const idNorm = String(step.id || '').trim().toLowerCase();

  if (idNorm === 'extra') {
    return 'extra';
  }

  const slug = idNorm.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  if (slug && /^[a-z]/.test(slug)) {
    return slug.length <= 48 ? slug : slug.slice(0, 48);
  }

  return 'other';
}

/**
 * Determine whether a step is complete given current selections.
 *
 * A required slot is considered complete when:
 * - The user has made a selection (stepSelections[idx] != null), OR
 * - The slot is not visible (isVisible === false) — hidden slots are auto-handled.
 *
 * @param {string} stepId - Step ID
 * @param {Object} selections - Current selections map
 * @param {Array} steps - Full steps array (needed to find container info)
 * @returns {boolean}
 */
export function isStepCompleteUtil(stepId, selections, steps) {
  if (stepId === 'cover') {
    return selections?.cover != null;
  }

  const step = steps.find((s) => s.id === stepId);

  if (!step) {
    return false;
  }

  if (typeof step.hasData === 'function' && !step.hasData()) {
    return true;
  }

  if (!step.container) {
    return false;
  }

  const values = step.container.values ?? [];

  if (values.length === 0) {
    return true;
  }

  const hasRequiredSlots = values.some((v) => v.isRequired);

  if (!hasRequiredSlots) {
    return true;
  }

  // A hidden container (isVisible=false) is considered fully auto-complete
  if (step.container.isVisible === false) {
    return true;
  }

  const stepSelections = selections?.[stepId];

  if (!stepSelections || !Array.isArray(stepSelections)) {
    return false;
  }

  const isRequiredVisibleSlotComplete = (value, idx) => {
    if (!value.isRequired) {
      return true;
    }

    if (stepSelections[idx] != null) {
      return true;
    }

    return value.isVisible === false;
  };

  // When master OGU has more slots than the set's selectedOptions, count filled entries
  // at each required visible slot index instead of matching by sequential index.
  if (values.length !== stepSelections.length) {
    const requiredVisibleCount = values.filter(
      (v) => v.isRequired && v.isVisible !== false
    ).length;
    const filledRequiredVisible = values.reduce((count, value, idx) => {
      if (!value.isRequired || value.isVisible === false) {
        return count;
      }

      return stepSelections[idx] != null ? count + 1 : count;
    }, 0);

    return filledRequiredVisible >= requiredVisibleCount;
  }

  return values.every(isRequiredVisibleSlotComplete);
}

/**
 * Whether all customize steps are satisfied for ATC.
 *
 * @param {Object|null|undefined} selections
 * @param {Array} steps
 * @param {{ skipCoverStep?: boolean, coverGroups?: unknown }} [options]
 * @returns {boolean}
 */
export function isCustomizeJourneyComplete(selections, steps, options = {}) {
  const {skipCoverStep = false, coverGroups} = options;

  if (!selections) return false;

  return steps.every((step) => {
    if (step.id === 'cover' && skipCoverStep) {
      return true;
    }

    if (step.id === 'cover' && coverGroups != null && typeof step.hasData === 'function') {
      if (!step.hasData(coverGroups)) {
        return true;
      }
    }

    return isStepCompleteUtil(step.id, selections, steps);
  });
}

/**
 * Determine whether a step is locked.
 * When enableLocking is false (inline mode), all steps are always unlocked.
 * When enableLocking is true (flyout mode), a step is locked if any previous step is incomplete.
 *
 * @param {number} stepIndex - 0-based index of the step
 * @param {Array} steps - Full steps array
 * @param {Object} selections - Current selections map
 * @param {boolean} enableLocking - Whether step locking is active
 * @returns {boolean}
 */
export function isStepLockedUtil(stepIndex, steps, selections, enableLocking) {
  if (!enableLocking) return false;
  if (stepIndex === 0) return false;

  return steps.slice(0, stepIndex).some((s) => !isStepCompleteUtil(s.id, selections, steps));
}

/**
 * Hook that provides step building and completion/locking utilities for the customize journey.
 *
 * @param {Object} product - SFCC product
 * @param {Object} selections - Current selections map
 * @param {Array} coverGroups - Cover groups derived from product
 * @param {boolean} [enableLocking=false] - Whether step locking is active (flyout mode)
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.isSimplePDP=false] - Whether this is a simple PDP (affects layout type resolution)
 * @returns {{ steps, isStepComplete, isStepLocked, getStepTitle }}
 */
export function useCustomizeSteps(product, selections, coverGroups, enableLocking = false, options = {}) {
  const intl = useIntl();
  const {isSimplePDP = false} = options;

  const steps = useMemo(
    () => buildSteps(product, {isSimplePDP}),
    [product, isSimplePDP]
  );

  const isStepComplete = useCallback(
    (stepId) => isStepCompleteUtil(stepId, selections, steps),
    [selections, steps]
  );

  const isStepLocked = useCallback(
    (stepIndex) => isStepLockedUtil(stepIndex, steps, selections, enableLocking),
    [steps, selections, enableLocking]
  );

  const getTitle = useCallback((step) => getStepTitle(step, intl), [intl]);

  const hasCoverData = coverGroups && coverGroups.length > 0;

  return {
    steps,
    hasCoverData,
    isStepComplete,
    isStepLocked,
    getStepTitle: getTitle,
  };
}
